import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import AppLink from '../../components/shared/AppLink';
import TripPlannerPromoHero from '../../components/shared/TripPlannerPromoHero';
import { getBeachConditionsDetail } from '../../services/beach-conditions/getBeachConditions';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';

const STATUS_META = {
  favorable_with_caution: {
    label: 'Favorable',
    className: 'bg-emerald-500/20 text-emerald-200 border-emerald-300/30',
  },
  use_extra_caution: {
    label: 'Use Extra Caution',
    className: 'bg-amber-500/20 text-amber-200 border-amber-300/30',
  },
  not_recommended_today: {
    label: 'Not Recommended Today',
    className: 'bg-red-500/25 text-red-100 border-red-300/40',
  },
  insufficient_current_data: {
    label: 'Insufficient Current Data',
    className: 'bg-slate-500/20 text-slate-200 border-slate-300/30',
  },
};

const LAND_ACTIVITY_KEYS = new Set(['shore_walk', 'sunbathing']);
const LAND_SAFETY_REASON_TOKENS = [
  'HIGH_SURF',
  'LARGE_WAVE',
  'HIGH_TIDE',
  'RUNUP',
  'THUNDERSTORM',
  'FLOOD',
  'HIGH_WIND',
];

function hasLandSafetyReason(reasonCodes = []) {
  return reasonCodes.some((code) =>
    LAND_SAFETY_REASON_TOKENS.some((token) => String(code || '').includes(token)));
}

function statusMetaForActivity(status, activityKey, reasonCodes = []) {
  const base = STATUS_META[status] || STATUS_META.insufficient_current_data;
  if (!LAND_ACTIVITY_KEYS.has(activityKey)) return base;

  if (status === 'favorable_with_caution') {
    return { ...base, label: 'Favorable' };
  }

  if (status === 'use_extra_caution') {
    if (hasLandSafetyReason(reasonCodes)) {
      return { ...base, label: 'Use Caution' };
    }
    return { ...base, label: 'Fair Conditions' };
  }

  if (status === 'not_recommended_today' && !hasLandSafetyReason(reasonCodes)) {
    return { ...base, label: 'Poor Conditions' };
  }

  return base;
}

function formatDate(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatDateCompact(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatNumber(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Number(value).toFixed(digits);
}

function scoreBand(score) {
  if (!Number.isFinite(score)) {
    return { label: 'No score', className: 'text-white/70' };
  }
  if (score >= 70) return { label: 'Favorable range', className: 'text-emerald-200' };
  if (score >= 45) return { label: 'Mixed range', className: 'text-amber-200' };
  return { label: 'Not recommended range', className: 'text-red-200' };
}

function humanizeReasonCode(code) {
  return String(code || '')
    .toLowerCase()
    .replaceAll('_', ' ')
    .trim();
}

function formatDelta(delta) {
  if (!Number.isFinite(delta) || delta === 0) return '0';
  return `${delta > 0 ? '+' : ''}${delta}`;
}

function renderConditionItem(label, value) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
      <div className="text-xs uppercase tracking-wide text-white/60">{label}</div>
      <div className="mt-1 text-base font-semibold text-white">{value}</div>
    </div>
  );
}

export default function BeachConditionsDetail() {
  const { slug } = useParams();
  const { island, islandConfig } = useMemo(() => getRuntimeSiteContext(), []);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showScoringMethodology, setShowScoringMethodology] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        setError('');
        const data = await getBeachConditionsDetail(slug, { island });
        if (cancelled) return;
        if (data?.notFound) {
          setError(data.message || 'Beach not found');
          return;
        }
        setPayload(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load beach detail');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [slug, island]);

  const beach = payload?.beach || null;
  const recommendations = useMemo(
    () =>
      (Array.isArray(payload?.recommendations) ? payload.recommendations : [])
        .slice()
        .sort((a, b) => Number(b.score || 0) - Number(a.score || 0)),
    [payload],
  );
  const advisories = Array.isArray(payload?.advisories) ? payload.advisories : [];
  const hazards = Array.isArray(payload?.hazards) ? payload.hazards : [];

  const buoy = payload?.conditions?.buoy || null;
  const tide = payload?.conditions?.tide || null;
  const forecast = payload?.conditions?.forecast || null;
  const forecastPeriods = Array.isArray(forecast?.periods) ? forecast.periods.slice(0, 4) : [];
  const dailySummary = payload?.narratives?.dailyBeachSummary || payload?.summary?.dailySummary || '';
  const dailySummaryGeneratedAt = payload?.narratives?.dailyBeachSummaryGeneratedAt || null;
  const dailySummaryError = payload?.narratives?.dailyBeachSummaryError || null;
  const summaryReasons = Array.isArray(payload?.summary?.reasonCodes) ? payload.summary.reasonCodes : [];
  const googleEmbedKey = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;
  const placeId = typeof beach?.placeID === 'string' ? beach.placeID.trim() : '';
  const hasPlaceId = Boolean(placeId);
  const mapLat = Number(beach?.location?.lat);
  const mapLng = Number(beach?.location?.lng);
  const hasMapCoords = Number.isFinite(mapLat) && Number.isFinite(mapLng);
  const mapEmbedSrc = hasPlaceId
    ? (googleEmbedKey
      ? `https://www.google.com/maps/embed/v1/place?key=${googleEmbedKey}&q=place_id:${placeId}`
      : `https://www.google.com/maps?q=place_id:${encodeURIComponent(placeId)}&output=embed`)
    : (hasMapCoords
      ? `https://www.google.com/maps?q=${mapLat},${mapLng}&z=14&output=embed`
      : '');
  const hasMapEmbed = Boolean(mapEmbedSrc);
  const webcams = Array.isArray(beach?.webcams) ? beach.webcams : [];
  const primaryWebcam = beach?.primaryWebcam || webcams[0] || null;
  const rawPrimaryWebcamEmbedUrl = typeof primaryWebcam?.embedUrl === 'string' ? primaryWebcam.embedUrl : '';
  const withQueryParams = (url, params) => {
    try {
      const parsed = new URL(url);
      Object.entries(params).forEach(([key, value]) => {
        if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, String(value));
      });
      return parsed.toString();
    } catch (error) {
      return url;
    }
  };
  const primaryWebcamEmbedUrl = primaryWebcam?.provider === 'youtube'
    ? withQueryParams(rawPrimaryWebcamEmbedUrl, {
      autoplay: 1,
      mute: 1,
      playsinline: 1,
      rel: 0,
    })
    : rawPrimaryWebcamEmbedUrl;
  const normalizeWebcamUrl = (value) => String(value || '').trim().replace(/\/+$/, '');
  const canInlineWebcam =
    Boolean(primaryWebcamEmbedUrl)
    && normalizeWebcamUrl(primaryWebcamEmbedUrl) !== normalizeWebcamUrl(primaryWebcam?.pageUrl);

  const sourceFreshnessRows = [
    { label: 'Payload generated', value: payload?.fetchedAt },
    { label: 'Buoy observed', value: buoy?.observedAt },
    { label: 'Tide observed', value: tide?.observedAt },
    { label: 'Forecast generated', value: forecast?.generatedAt },
    { label: 'Forecast updated', value: forecast?.updateTime },
  ];

  return (
    <>
      <Helmet>
        <title>{beach ? `${beach.name} Beach Conditions | Alakaʻi Aloha` : 'Beach Conditions | Alakaʻi Aloha'}</title>
        <meta
          name="description"
          content={
            beach
              ? `Today’s activity-focused beach conditions for ${beach.name} on ${islandConfig.title}.`
              : `Today’s activity-focused ${islandConfig.title} beach conditions.`
          }
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl(`/beach-conditions/${slug || ''}`)} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 pt-2 pb-10 space-y-8">
        <div className="text-sm">
          <Link to="/beach-conditions" className="underline hover:text-green-300">
            ← Back to beach conditions index
          </Link>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/15 bg-white/5 px-4 py-8 text-white/70">
            Loading beach conditions...
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-lg border border-red-300/50 bg-red-900/20 px-4 py-8 text-red-100">
            <p className="font-semibold">Beach detail unavailable.</p>
            <p className="mt-2 text-sm">{error}</p>
          </div>
        ) : null}

        {!loading && !error && beach ? (
          <>
            <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-5">
              <header className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-5 space-y-4">
                <h1 className="text-4xl font-bold">{beach.name}</h1>

                {beach.area ? (
                  <div className="text-xs uppercase tracking-wide text-white/60">{beach.area}</div>
                ) : null}

                {dailySummary ? (
                  <div className="rounded-lg border border-emerald-300/35 bg-emerald-900/20 p-4">
                    <div className="text-xs uppercase tracking-wide text-emerald-200/90">Today&apos;s guidance</div>
                    <p className="mt-2 text-sm text-emerald-50">{dailySummary}</p>
                    {dailySummaryGeneratedAt ? (
                      <div className="mt-2 text-[11px] text-emerald-100/85">
                        Updated: {formatDateCompact(dailySummaryGeneratedAt)}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-lg border border-amber-300/40 bg-amber-900/20 p-4 text-sm text-amber-100">
                    Daily narrative is unavailable for this beach in the latest conditions run.
                    {dailySummaryError ? (
                      <div className="mt-2 text-xs text-amber-200/90">{dailySummaryError}</div>
                    ) : null}
                  </div>
                )}

                {summaryReasons.length > 0 ? (
                  <div>
                    <div className="mb-1 text-[11px] uppercase tracking-wide text-white/55">Cross-activity factors</div>
                    <div className="flex flex-wrap gap-2">
                      {summaryReasons.slice(0, 6).map((code) => (
                        <span key={`summary-${code}`} className="rounded-full border border-white/20 px-2 py-0.5 text-[11px] text-white/75">
                          {code.replaceAll('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </header>

              <figure className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-3">
                {beach.imageUrl ? (
                  <img
                    src={beach.imageUrl}
                    alt={`${beach.name} shoreline in ideal conditions`}
                    className="h-64 w-full rounded-lg object-cover"
                  />
                ) : (
                  <div className="h-64 w-full rounded-lg border border-white/15 bg-white/5 grid place-items-center text-sm text-white/50">
                    Beach image unavailable
                  </div>
                )}
                <figcaption className="mt-2 text-xs text-white/60">
                  {beach.name} under ideal conditions (reference photo, not live).
                </figcaption>

                {primaryWebcam ? (
                  <div className="mt-4 rounded-lg border border-white/15 bg-white/[0.06] backdrop-blur-sm p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-xs uppercase tracking-wide text-white/60">
                        Live Webcam
                      </div>
                      {primaryWebcam.nearby ? (
                        <span className="rounded-full border border-amber-300/45 bg-amber-900/25 px-2 py-0.5 text-[11px] text-amber-100">
                          Nearby View
                        </span>
                      ) : null}
                    </div>

                    {canInlineWebcam ? (
                      <div className="mt-2 overflow-hidden rounded-md border border-white/15">
                        <iframe
                          src={primaryWebcamEmbedUrl}
                          title={`${primaryWebcam.title} at ${beach.name}`}
                          className="h-64 w-full bg-black"
                          loading="lazy"
                          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="mt-2 rounded-md border border-amber-300/40 bg-amber-900/20 px-3 py-2 text-xs text-amber-100">
                        This camera does not provide an embeddable player.
                      </div>
                    )}

                    {primaryWebcam.note ? (
                      <p className="mt-2 text-xs text-white/70">{primaryWebcam.note}</p>
                    ) : null}

                    <div className="mt-2 text-xs">
                      <a
                        href={primaryWebcam.pageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-green-300"
                      >
                        Open webcam in a new tab
                      </a>
                    </div>
                  </div>
                ) : null}
              </figure>
            </section>

            <section className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-5 space-y-3">
              <h2 className="text-2xl font-semibold">About This Beach</h2>
              {beach.shortDescription ? (
                <p className="text-white/80">{beach.shortDescription}</p>
              ) : (
                <p className="text-white/70">Evergreen profile content is currently unavailable for this beach.</p>
              )}
              <div className="text-sm">
                <AppLink to={beach?.links?.activityProfile || `/activities/${slug}`} className="underline hover:text-green-300">
                  View evergreen beach profile
                </AppLink>
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl font-semibold">Activity Recommendations Today</h2>
                <button
                  type="button"
                  onClick={() => setShowScoringMethodology((prev) => !prev)}
                  className="rounded-full border border-white/25 bg-white/[0.08] px-3 py-1 text-xs text-white/85 hover:bg-white/[0.14]"
                >
                  {showScoringMethodology ? 'Hide scoring methodology' : 'Show scoring methodology'}
                </button>
              </div>
              {showScoringMethodology ? (
                <div className="text-xs text-white/65">
                  Scores combine evergreen baselines with live-condition adjustments and factor-based deltas.
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {recommendations.map((recommendation) => {
                  const meta = statusMetaForActivity(
                    recommendation.status,
                    recommendation.key,
                    recommendation.reasonCodes,
                  );
                  const band = scoreBand(recommendation.score);
                  const hasBaseline = Number.isFinite(recommendation.baselineScore);
                  const hasNetDelta = Number.isFinite(recommendation.netDelta);
                  const scoreImpacts = Array.isArray(recommendation.scoreImpacts) ? recommendation.scoreImpacts : [];
                  return (
                    <article key={recommendation.key} className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-4">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold">{recommendation.label}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full border ${meta.className}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-white/70">
                        Score: <span className="font-semibold text-white">{recommendation.score}/100</span> | Confidence:{' '}
                        <span className="font-semibold text-white">{recommendation.confidence}</span>
                      </div>
                      {showScoringMethodology ? (
                        <>
                          {hasBaseline || hasNetDelta ? (
                            <div className="mt-1 text-xs text-white/65">
                              Baseline {hasBaseline ? recommendation.baselineScore : '—'}
                              {hasNetDelta ? (
                                <>
                                  {' '}• live adjustment{' '}
                                  <span className={recommendation.netDelta >= 0 ? 'text-emerald-200' : 'text-red-200'}>
                                    {formatDelta(recommendation.netDelta)}
                                  </span>
                                </>
                              ) : null}
                            </div>
                          ) : null}
                          <div className={`mt-1 text-xs ${band.className}`}>{band.label}</div>

                          {scoreImpacts.length > 0 ? (
                            <div className="mt-3">
                              <div className="text-[11px] uppercase tracking-wide text-white/55">Score drivers</div>
                              <ul className="mt-2 space-y-1.5 text-xs">
                                {scoreImpacts.slice(0, 4).map((impact) => (
                                  <li key={`${recommendation.key}-${impact.reasonCode}`} className="flex items-start justify-between gap-3">
                                    <span className="text-white/80">{humanizeReasonCode(impact.reasonCode)}</span>
                                    <span className={`font-semibold ${impact.delta > 0 ? 'text-emerald-200' : 'text-red-200'}`}>
                                      {formatDelta(impact.delta)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}

                          {Array.isArray(recommendation.reasonCodes) && recommendation.reasonCodes.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {recommendation.reasonCodes.map((code) => (
                                <span
                                  key={`${recommendation.key}-${code}`}
                                  className="rounded-full border border-white/20 px-2 py-0.5 text-[11px] text-white/75"
                                >
                                  {humanizeReasonCode(code)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-2xl font-semibold">Current Conditions Data</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {renderConditionItem('Wave Height', `${formatNumber(buoy?.waveHeightFt)} ft`)}
                {renderConditionItem('Dominant Period', `${formatNumber(buoy?.dominantPeriodSec, 0)} s`)}
                {renderConditionItem('Water Temp', `${formatNumber(buoy?.waterTempF)} °F`)}
                {renderConditionItem('Current Tide', `${formatNumber(tide?.waterLevelFt)} ft`)}
              </div>

              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <div className="font-semibold text-white">Data Freshness</div>
                <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-white/75">
                  {sourceFreshnessRows.map((row) => (
                    <li key={row.label}>
                      <span className="text-white/60">{row.label}:</span> {formatDate(row.value)}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-white/75">
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="font-semibold text-white">Buoy Observation</div>
                  <div className="mt-2">Observed: {formatDate(buoy?.observedAt)}</div>
                  <div className="mt-1">Direction: {buoy?.directionDeg ?? '—'}°</div>
                </div>

                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="font-semibold text-white">Tide Snapshot</div>
                  <div className="mt-2">Observed: {formatDate(tide?.observedAt)}</div>
                  {Array.isArray(tide?.nextHiLo) && tide.nextHiLo.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {tide.nextHiLo.map((item, idx) => (
                        <li key={`${item.timeLocal || idx}-${idx}`}>
                          {item.type}: {item.timeLocal} ({formatNumber(item.heightFt)} ft)
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>

              {forecast ? (
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="font-semibold text-white">Hourly Beach Forecast (Next 4h)</div>
                  <div className="mt-1 text-xs text-white/60">
                    Generated: {formatDate(forecast.generatedAt)} | Updated: {formatDate(forecast.updateTime)}
                  </div>
                  <div className="mt-3 space-y-2 md:hidden">
                    {forecastPeriods.map((period, idx) => (
                      <div key={`${period.startTime || idx}-${idx}`} className="rounded-lg border border-white/10 bg-white/[0.04] p-3 text-sm">
                        <div className="font-semibold text-white">{formatDate(period.startTime)}</div>
                        <div className="mt-1 text-white/85">{period.shortForecast || '—'}</div>
                        <div className="mt-1 text-white/75">
                          Temp: {period.temperature ?? '—'} {period.temperatureUnit || ''} • Rain: {period.precipitationProbabilityPct ?? '—'}%
                        </div>
                        <div className="text-white/75">
                          Wind: {period.windSpeedMph ?? '—'} mph {period.windDirection || ''}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 overflow-x-auto hidden md:block">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-white/70">
                          <th className="pr-4 pb-2">Start</th>
                          <th className="pr-4 pb-2">Forecast</th>
                          <th className="pr-4 pb-2">Temp</th>
                          <th className="pr-4 pb-2">Rain %</th>
                          <th className="pr-4 pb-2">Wind</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecastPeriods.map((period, idx) => (
                          <tr key={`${period.startTime || idx}-${idx}`} className="border-t border-white/10">
                            <td className="py-2 pr-4 whitespace-nowrap">{formatDate(period.startTime)}</td>
                            <td className="py-2 pr-4">{period.shortForecast || '—'}</td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {period.temperature ?? '—'} {period.temperatureUnit || ''}
                            </td>
                            <td className="py-2 pr-4 whitespace-nowrap">{period.precipitationProbabilityPct ?? '—'}</td>
                            <td className="py-2 pr-4 whitespace-nowrap">
                              {period.windSpeedMph ?? '—'} mph {period.windDirection || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-300/40 bg-amber-900/20 p-4 text-sm text-amber-100">
                  Forecast details are unavailable for this beach because coordinate data is missing.
                </div>
              )}
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <article className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-4">
                <h2 className="text-xl font-semibold">CWB Advisories / Notifications</h2>
                {advisories.length === 0 ? (
                  <p className="mt-2 text-sm text-white/70">No active matching CWB events were found for this beach.</p>
                ) : (
                  <ul className="mt-3 space-y-3 text-sm">
                    {advisories.map((event) => (
                      <li key={event.id} className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <div className="font-semibold">{event.type || 'Event'}</div>
                        <div className="text-white/80">{event.title || event.locationName || 'No title provided'}</div>
                        <div className="mt-1 text-xs text-white/60">Posted: {formatDate(event.postedDate)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </article>

              <article className="rounded-lg border border-white/20 bg-white/[0.08] backdrop-blur-md p-4">
                <h2 className="text-xl font-semibold">NWS Hazards</h2>
                {hazards.length === 0 ? (
                  <p className="mt-2 text-sm text-white/70">No active NWS hazards were found for this beach zone.</p>
                ) : (
                  <ul className="mt-3 space-y-3 text-sm">
                    {hazards.map((hazard, idx) => (
                      <li key={`${hazard.event || 'hazard'}-${idx}`} className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <div className="font-semibold">{hazard.event || 'Hazard'}</div>
                        <div className="text-white/80">{hazard.headline || hazard.areaDesc || 'No headline provided'}</div>
                        <div className="mt-1 text-xs text-white/60">Sent: {formatDate(hazard.sent)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            </section>

            {hasMapEmbed ? (
              <iframe
                title={`${beach.name} map`}
                src={mapEmbedSrc}
                className="h-72 w-full bg-black md:h-[420px]"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
              />
            ) : (
              <p className="text-sm text-white/70">
                Location map is unavailable for this beach.
              </p>
            )}
          </>
        ) : null}
      </div>

      <TripPlannerPromoHero pageType="activity" className="mt-8" />
    </>
  );
}
